import React, { useState, useEffect, useCallback } from 'react';
import api from './api/client';

// ─── Tokens ────────────────────────────────────────────────
const C = {
  bg: '#1a1a2e', surface: '#0F1218', card: '#161B26', cardHov: '#1C2233',
  border: '#1E2535', borderHi: '#2A3550',
  accent: '#00D4A0', accentDim: '#00D4A015',
  gold: '#F0A500', goldDim: '#F0A50015',
  danger: '#FF4560', dangerDim: '#FF456015',
  info: '#3D8EFF', infoDim: '#3D8EFF15',
  text: '#E8EDF8', sub: '#7A8699', muted: '#404B60',
  font: "'DM Sans', sans-serif",
};

const fmt = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
const now = new Date();

// ─── Mini componentes ──────────────────────────────────────
const KPICard = ({ label, value, color = C.accent, sub }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 160 }}>
    <div style={{ fontSize: 11, color: C.sub, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 900, color, fontFamily: C.font }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
  </div>
);

const Chip = ({ label, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{label}</span>
);

const Btn = ({ children, onClick, variant = 'primary', small, loading, disabled, style = {} }) => {
  const base = { background: 'none', border: 'none', cursor: 'pointer', fontFamily: C.font, fontWeight: 700, borderRadius: 8, transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 6 };
  const variants = {
    primary: { background: C.accent, color: '#000', padding: small ? '6px 14px' : '9px 20px', fontSize: small ? 12 : 14 },
    ghost: { background: 'transparent', color: C.sub, border: `1px solid ${C.border}`, padding: small ? '5px 12px' : '8px 18px', fontSize: small ? 12 : 14 },
    danger: { background: C.dangerDim, color: C.danger, border: `1px solid ${C.danger}44`, padding: small ? '5px 12px' : '8px 18px', fontSize: small ? 12 : 14 },
    gold: { background: C.goldDim, color: C.gold, border: `1px solid ${C.gold}44`, padding: small ? '5px 12px' : '8px 18px', fontSize: small ? 12 : 14 },
    info: { background: C.infoDim, color: C.info, border: `1px solid ${C.info}44`, padding: small ? '5px 12px' : '8px 18px', fontSize: small ? 12 : 14 },
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{ ...base, ...variants[variant], opacity: (disabled || loading) ? 0.5 : 1, ...style }}>
      {loading ? '...' : children}
    </button>
  );
};

const Input = ({ label, type = 'text', value, onChange, placeholder, required, style = {} }) => (
  <div style={{ marginBottom: 14, ...style }}>
    {label && <div style={{ fontSize: 11, color: C.sub, fontWeight: 700, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}{required && <span style={{ color: C.danger }}> *</span>}</div>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none', boxSizing: 'border-box' }} />
  </div>
);

const Select = ({ label, value, onChange, options, required, style = {} }) => (
  <div style={{ marginBottom: 14, ...style }}>
    {label && <div style={{ fontSize: 11, color: C.sub, fontWeight: 700, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}{required && <span style={{ color: C.danger }}> *</span>}</div>}
    <select value={value} onChange={onChange} style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none', boxSizing: 'border-box' }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Alert = ({ children, type = 'info' }) => {
  const colors = { info: [C.infoDim, C.info], danger: [C.dangerDim, C.danger], success: [C.accentDim, C.accent] };
  const [bg, color] = colors[type] || colors.info;
  return <div style={{ background: bg, border: `1px solid ${color}44`, color, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{children}</div>;
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
    <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
  </div>
);

// Gráfico de barras simples
const BarChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.flatMap((d) => [d.revenue, d.expenses]), 1);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height }}>
        {data.map((d) => (
          <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: height - 20 }}>
              <div style={{ flex: 1, height: `${(d.revenue / max) * (height - 20)}px`, background: C.accent, borderRadius: '3px 3px 0 0', minHeight: d.revenue > 0 ? 2 : 0 }} title={`Receita: ${fmt(d.revenue)}`} />
              <div style={{ flex: 1, height: `${(d.expenses / max) * (height - 20)}px`, background: C.danger, borderRadius: '3px 3px 0 0', minHeight: d.expenses > 0 ? 2 : 0 }} title={`Despesa: ${fmt(d.expenses)}`} />
            </div>
            <div style={{ fontSize: 9, color: C.muted, textAlign: 'center', whiteSpace: 'nowrap' }}>{d.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, background: C.accent, borderRadius: 2 }} /><span style={{ fontSize: 11, color: C.sub }}>Receita</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, background: C.danger, borderRadius: 2 }} /><span style={{ fontSize: 11, color: C.sub }}>Despesa</span></div>
      </div>
    </div>
  );
};

// ─── DASHBOARD ────────────────────────────────────────────
const AccDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await api.accounting.summary({ year, month })); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][i] }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: C.sub, fontSize: 13 }}>Período:</span>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '6px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '6px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
        <Btn variant="ghost" small onClick={load}>↻ Atualizar</Btn>
      </div>

      {error && <Alert type="danger">{error}</Alert>}
      {loading ? <Spinner /> : data && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
            <KPICard label="Receita do mês" value={fmt(data.monthRevenue)} color={C.accent} />
            <KPICard label="Despesa do mês" value={fmt(data.monthExpenses)} color={C.danger} />
            <KPICard label="Resultado líquido" value={fmt(data.netResult)} color={data.netResult >= 0 ? C.accent : C.danger} sub={data.netResult >= 0 ? '▲ positivo' : '▼ negativo'} />
            <KPICard label="Saldo em caixa" value={fmt(data.cashBalance)} color={C.gold} sub="Histórico acumulado" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 16, fontSize: 13 }}>Receitas x Despesas — últimos 6 meses</div>
              <BarChart data={data.last6Months} height={140} />
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 16, fontSize: 13 }}>Últimos 10 lançamentos</div>
              {(data.recentEntries || []).slice(0, 10).map((e) => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{e.description?.slice(0, 38)}</div>
                    <div style={{ fontSize: 10, color: C.sub }}>{fmtDate(e.date)} · {e.category}</div>
                  </div>
                  <span style={{ color: e.type === 'receita' ? C.accent : C.danger, fontWeight: 800, fontSize: 13 }}>
                    {e.type === 'receita' ? '+' : '-'}{fmt(e.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── LANÇAMENTOS ──────────────────────────────────────────
const AccEntries = ({ categories }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ year: now.getFullYear(), month: now.getMonth() + 1, type: '', status: '' });
  const [form, setForm] = useState({ type: 'receita', category: '', subcategory: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'pago', paymentMethod: 'pix', notes: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { page, limit: 50, ...filters };
      if (!params.type) delete params.type;
      if (!params.status) delete params.status;
      const res = await api.accounting.entries(params);
      setEntries(res.entries || []); setTotal(res.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ type: 'receita', category: '', subcategory: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'pago', paymentMethod: 'pix', notes: '' }); setShowForm(true); };
  const openEdit = (e) => { setEditing(e); setForm({ type: e.type, category: e.category, subcategory: e.subcategory || '', description: e.description, amount: String(e.amount), date: e.date, status: e.status, paymentMethod: e.paymentMethod || 'pix', notes: e.notes || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.type || !form.category || !form.description || !form.amount || !form.date) return setError('Preencha todos os campos obrigatórios.');
    setSaving(true); setError('');
    try {
      if (editing) await api.accounting.updateEntry(editing.id, form);
      else await api.accounting.createEntry(form);
      setShowForm(false); load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const del = async (e) => {
    if (!window.confirm(`Excluir "${e.description}"?`)) return;
    setDeleting(e.id);
    try { await api.accounting.deleteEntry(e.id); load(); }
    catch (err) { setError(err.message); }
    finally { setDeleting(null); }
  };

  const catOptions = [{ value: '', label: '— Categoria —' }, ...(categories || []).filter((c) => !form.type || c.type === form.type).map((c) => ({ value: c.name, label: c.name }))];
  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: (i + 1).toString().padStart(2, '0') }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <select value={filters.month} onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={filters.year} onChange={(e) => setFilters((f) => ({ ...f, year: e.target.value }))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
        <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          <option value="">Todos os tipos</option>
          <option value="receita">Receita</option>
          <option value="despesa">Despesa</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          <option value="">Todos os status</option>
          <option value="pago">Pago</option>
          <option value="pendente">Pendente</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <Btn variant="primary" small onClick={openNew}>+ Novo Lançamento</Btn>
        <span style={{ fontSize: 12, color: C.sub, marginLeft: 'auto' }}>{total} lançamento(s)</span>
      </div>

      {error && <Alert type="danger">{error}</Alert>}

      {/* Modal lançamento */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000CC', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{editing ? 'Editar Lançamento' : 'Novo Lançamento'}</div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Select label="Tipo *" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, category: '' }))} options={[{ value: 'receita', label: 'Receita' }, { value: 'despesa', label: 'Despesa' }]} />
              <Select label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} options={[{ value: 'pago', label: 'Pago' }, { value: 'pendente', label: 'Pendente' }, { value: 'cancelado', label: 'Cancelado' }]} />
            </div>
            <Select label="Categoria *" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} options={catOptions} />
            <Input label="Descrição *" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descrição do lançamento" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Valor (R$) *" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
              <Input label="Data *" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input label="Subcategoria" value={form.subcategory} onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))} placeholder="Opcional" />
              <Select label="Pagamento" value={form.paymentMethod} onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))} options={[{ value: 'pix', label: 'PIX' }, { value: 'boleto', label: 'Boleto' }, { value: 'cartao', label: 'Cartão' }, { value: 'transferencia', label: 'Transferência' }]} />
            </div>
            <Input label="Observações" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Btn>
              <Btn variant="primary" onClick={save} loading={saving}>Salvar</Btn>
            </div>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Data', 'Descrição', 'Categoria', 'Tipo', 'Status', 'Valor', 'Ações'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: C.sub, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.muted }}>Nenhum lançamento no período</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} style={{ borderBottom: `1px solid ${C.border}26` }}>
                  <td style={{ padding: '10px 12px', color: C.sub, whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</td>
                  <td style={{ padding: '10px 12px', color: C.text, maxWidth: 220 }}>
                    <div style={{ fontWeight: 600 }}>{e.description}</div>
                    {e.automatic && <span style={{ fontSize: 9, background: C.infoDim, color: C.info, borderRadius: 4, padding: '1px 5px', marginTop: 2, display: 'inline-block' }}>AUTO</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}>{e.category}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <Chip label={e.type === 'receita' ? 'Receita' : 'Despesa'} color={e.type === 'receita' ? C.accent : C.danger} />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Chip label={e.status} color={e.status === 'pago' ? C.accent : e.status === 'pendente' ? C.gold : C.muted} />
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: e.type === 'receita' ? C.accent : C.danger, whiteSpace: 'nowrap' }}>
                    {e.type === 'receita' ? '+' : '-'}{fmt(e.amount)}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {!e.automatic && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Btn variant="ghost" small onClick={() => openEdit(e)}>Editar</Btn>
                        <Btn variant="danger" small loading={deleting === e.id} onClick={() => del(e)}>Excluir</Btn>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > 50 && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
              <Btn variant="ghost" small disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Anterior</Btn>
              <span style={{ color: C.sub, fontSize: 13, padding: '6px 10px' }}>Página {page} de {Math.ceil(total / 50)}</span>
              <Btn variant="ghost" small disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}>Próxima →</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── CATEGORIAS ───────────────────────────────────────────
const AccCategories = ({ categories, reload }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'receita', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!form.name) return setError('Nome obrigatório.');
    setSaving(true); setError('');
    try { await api.accounting.createCategory(form); setShowForm(false); setForm({ name: '', type: 'receita', description: '' }); reload(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const del = async (cat) => {
    if (!window.confirm(`Desativar categoria "${cat.name}"?`)) return;
    try { await api.accounting.deleteCategory(cat.id); reload(); }
    catch (e) { setError(e.message); }
  };

  const receitas = (categories || []).filter((c) => c.type === 'receita');
  const despesas = (categories || []).filter((c) => c.type === 'despesa');

  return (
    <div>
      {error && <Alert type="danger">{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn variant="primary" small onClick={() => setShowForm(!showForm)}>+ Nova Categoria</Btn>
      </div>

      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>Nova Categoria</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Nome *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome da categoria" />
            <Select label="Tipo *" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} options={[{ value: 'receita', label: 'Receita' }, { value: 'despesa', label: 'Despesa' }]} />
          </div>
          <Input label="Descrição" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descrição opcional" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="primary" small onClick={save} loading={saving}>Salvar</Btn>
            <Btn variant="ghost" small onClick={() => setShowForm(false)}>Cancelar</Btn>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {[{ label: 'Receitas', list: receitas, color: C.accent }, { label: 'Despesas', list: despesas, color: C.danger }].map(({ label, list, color }) => (
          <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, background: color + '12' }}>
              <span style={{ fontWeight: 800, color, fontSize: 13 }}>{label} ({list.length})</span>
            </div>
            {list.map((cat) => (
              <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: `1px solid ${C.border}22` }}>
                <div>
                  <div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{cat.name}</div>
                  {cat.description && <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{cat.description}</div>}
                </div>
                <Btn variant="danger" small onClick={() => del(cat)}>Remover</Btn>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── DRE ──────────────────────────────────────────────────
const AccDRE = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try { setData(await api.accounting.dre({ year, month })); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const doExport = async (type) => {
    setExporting(type);
    try { await api.accounting.downloadExport(type, { type: 'dre', year, month }); }
    catch (e) { setError(e.message); }
    finally { setExporting(''); }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: (i + 1).toString().padStart(2, '0') }));

  const DRELine = ({ label, value, indent = false, bold = false, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}22`, paddingLeft: indent ? 20 : 0 }}>
      <span style={{ color: color || (bold ? C.text : C.sub), fontWeight: bold ? 800 : 500, fontSize: 13 }}>{label}</span>
      <span style={{ color: color || (bold ? C.text : C.sub), fontWeight: bold ? 800 : 600, fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(value)}</span>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
        <Btn variant="primary" small onClick={load}>Gerar DRE</Btn>
        {data && (<>
          <Btn variant="ghost" small loading={exporting === 'pdf'} onClick={() => doExport('pdf')}>📄 PDF</Btn>
          <Btn variant="ghost" small loading={exporting === 'excel'} onClick={() => doExport('excel')}>📊 Excel</Btn>
        </>)}
      </div>

      {error && <Alert type="danger">{error}</Alert>}
      {loading && <Spinner />}
      {!loading && data && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, maxWidth: 680 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>+Qserve</div>
            <div style={{ fontSize: 14, color: C.sub, marginTop: 4 }}>Demonstrativo de Resultado do Exercício (DRE)</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Período: {String(month).padStart(2, '0')}/{year}</div>
          </div>

          <div style={{ background: C.accentDim, borderRadius: 8, padding: '8px 14px', marginBottom: 16 }}>
            <div style={{ fontWeight: 800, color: C.accent, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>Receitas</div>
          </div>
          {Object.entries(data.revenues?.byCategory || {}).map(([cat, amt]) => (
            <DRELine key={cat} label={`(+) ${cat}`} value={amt} indent />
          ))}
          <DRELine label="= RECEITA BRUTA" value={data.revenues?.total} bold color={C.accent} />

          <div style={{ height: 16 }} />
          <div style={{ background: C.dangerDim, borderRadius: 8, padding: '8px 14px', marginBottom: 16 }}>
            <div style={{ fontWeight: 800, color: C.danger, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>Despesas Operacionais</div>
          </div>
          {Object.entries(data.expenses?.byCategory || {}).map(([cat, amt]) => (
            <DRELine key={cat} label={`(-) ${cat}`} value={amt} indent />
          ))}
          <DRELine label="= TOTAL DESPESAS" value={data.expenses?.total} bold color={C.danger} />

          <div style={{ height: 20, borderTop: `2px solid ${C.borderHi}`, marginTop: 8 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0' }}>
            <span style={{ fontSize: 16, fontWeight: 900, color: C.text }}>= RESULTADO LÍQUIDO</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: data.netResult >= 0 ? C.accent : C.danger, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(data.netResult)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── FLUXO DE CAIXA ───────────────────────────────────────
const AccCashFlow = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try { setData(await api.accounting.cashflow({ year, month })); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const doExport = async (type) => {
    setExporting(type);
    try { await api.accounting.downloadExport(type, { type: 'entries', year, month }); }
    catch (e) { setError(e.message); }
    finally { setExporting(''); }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: (i + 1).toString().padStart(2, '0') }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
        <Btn variant="primary" small onClick={load}>Gerar Fluxo</Btn>
        {data && (<>
          <Btn variant="ghost" small loading={exporting === 'pdf'} onClick={() => doExport('pdf')}>📄 PDF</Btn>
          <Btn variant="ghost" small loading={exporting === 'excel'} onClick={() => doExport('excel')}>📊 Excel</Btn>
        </>)}
      </div>

      {error && <Alert type="danger">{error}</Alert>}
      {loading && <Spinner />}
      {!loading && data && (
        <>
          {/* Mini gráfico de barras */}
          {data.rows?.length > 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 13, marginBottom: 14 }}>Fluxo diário — {String(month).padStart(2, '0')}/{year}</div>
              <BarChart data={data.rows.slice(0, 30).map((r) => ({ label: r.date.slice(8), revenue: r.revenue, expenses: r.expenses }))} height={120} />
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Data', 'Entradas', 'Saídas', 'Líquido', 'Saldo Acum.', 'Projetado'].map((h) => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'right', color: C.sub, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, ':first': { textAlign: 'left' } }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows?.map((r) => (
                  <tr key={r.date} style={{ borderBottom: `1px solid ${C.border}22` }}>
                    <td style={{ padding: '8px 12px', color: C.sub, fontFamily: C.mono, fontSize: 12 }}>{fmtDate(r.date)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: C.accent, fontWeight: 600 }}>{r.revenue > 0 ? fmt(r.revenue) : '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: C.danger, fontWeight: 600 }}>{r.expenses > 0 ? fmt(r.expenses) : '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: r.net >= 0 ? C.accent : C.danger, fontWeight: 700 }}>{r.net !== 0 ? fmt(r.net) : '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: C.text, fontWeight: 800, fontFamily: C.mono, fontSize: 12 }}>{fmt(r.balance)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: C.gold, fontSize: 12 }}>{r.projected !== 0 ? fmt(r.projected) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── EXTRATO BANCÁRIO ─────────────────────────────────────
const AccBank = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', amount: '', type: 'credito', notes: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const res = await api.accounting.bank({ year, month }); setStatements(res.statements || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date || !form.description || !form.amount) return setError('Preencha todos os campos.');
    setSaving(true); setError('');
    try { await api.accounting.createBank(form); setShowForm(false); setForm({ date: new Date().toISOString().split('T')[0], description: '', amount: '', type: 'credito', notes: '' }); load(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const reconcile = async (stmt) => {
    try { await api.accounting.reconcile(stmt.id, null); load(); }
    catch (e) { setError(e.message); }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: (i + 1).toString().padStart(2, '0') }));
  const totalCredito = statements.filter((s) => s.type === 'credito').reduce((sum, s) => sum + parseFloat(s.amount), 0);
  const totalDebito = statements.filter((s) => s.type === 'debito').reduce((sum, s) => sum + parseFloat(s.amount), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
        <Btn variant="primary" small onClick={() => setShowForm(!showForm)}>+ Inserir Lançamento</Btn>
      </div>

      {error && <Alert type="danger">{error}</Alert>}

      <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
        <KPICard label="Créditos" value={fmt(totalCredito)} color={C.accent} />
        <KPICard label="Débitos" value={fmt(totalDebito)} color={C.danger} />
        <KPICard label="Saldo período" value={fmt(totalCredito - totalDebito)} color={totalCredito >= totalDebito ? C.accent : C.danger} />
      </div>

      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>Novo Lançamento Bancário</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <Input label="Data *" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            <Input label="Valor (R$) *" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
            <Select label="Tipo *" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} options={[{ value: 'credito', label: 'Crédito' }, { value: 'debito', label: 'Débito' }]} />
          </div>
          <Input label="Descrição *" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descrição do lançamento" />
          <Input label="Observações" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="primary" small onClick={save} loading={saving}>Salvar</Btn>
            <Btn variant="ghost" small onClick={() => setShowForm(false)}>Cancelar</Btn>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Data', 'Descrição', 'Tipo', 'Valor', 'Saldo', 'Conciliado', 'Ação'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: C.sub, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {statements.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.muted }}>Nenhum lançamento bancário no período</td></tr>
              )}
              {statements.map((s) => (
                <tr key={s.id} style={{ borderBottom: `1px solid ${C.border}26` }}>
                  <td style={{ padding: '9px 12px', color: C.sub, whiteSpace: 'nowrap' }}>{fmtDate(s.date)}</td>
                  <td style={{ padding: '9px 12px', color: C.text, fontWeight: 600 }}>{s.description}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <Chip label={s.type === 'credito' ? 'Crédito' : 'Débito'} color={s.type === 'credito' ? C.accent : C.danger} />
                  </td>
                  <td style={{ padding: '9px 12px', fontWeight: 800, color: s.type === 'credito' ? C.accent : C.danger }}>
                    {s.type === 'credito' ? '+' : '-'}{fmt(s.amount)}
                  </td>
                  <td style={{ padding: '9px 12px', color: C.text, fontFamily: C.mono, fontSize: 12 }}>{fmt(s.balance)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <Chip label={s.reconciled ? '✓ Sim' : 'Não'} color={s.reconciled ? C.accent : C.muted} />
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {!s.reconciled && <Btn variant="ghost" small onClick={() => reconcile(s)}>Conciliar</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── RELATÓRIOS ───────────────────────────────────────────
const AccReports = () => {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  const doExport = async (type, reportType) => {
    setExporting(`${type}-${reportType}`); setError('');
    try { await api.accounting.downloadExport(type, { type: reportType, year, month }); }
    catch (e) { setError(e.message); }
    finally { setExporting(''); }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][i] }));

  const reports = [
    { id: 'dre', title: 'DRE — Demonstrativo de Resultado', desc: 'Receitas, despesas e resultado líquido do período', icon: '📈' },
    { id: 'entries', title: 'Lançamentos do Período', desc: 'Lista completa de todos os lançamentos', icon: '📋' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: C.sub, fontSize: 13 }}>Período:</span>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: C.font }}>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '7px 10px', width: 80, fontSize: 13, fontFamily: C.font }} />
      </div>

      {error && <Alert type="danger">{error}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {reports.map((r) => (
          <div key={r.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>{r.icon}</div>
            <div style={{ fontWeight: 800, color: C.text, fontSize: 15, marginBottom: 6 }}>{r.title}</div>
            <div style={{ color: C.sub, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>{r.desc}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" small loading={exporting === `pdf-${r.id}`} onClick={() => doExport('pdf', r.id)}>📄 PDF</Btn>
              <Btn variant="ghost" small loading={exporting === `excel-${r.id}`} onClick={() => doExport('excel', r.id)}>📊 Excel</Btn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── MÓDULO PRINCIPAL ─────────────────────────────────────
export default function AccountingModule() {
  const [tab, setTab] = useState('dashboard');
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try { const res = await api.accounting.categories(); setCategories(res.categories || []); }
    catch (_) {}
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'entries', label: 'Lançamentos', icon: '📋' },
    { id: 'categories', label: 'Categorias', icon: '🏷️' },
    { id: 'dre', label: 'DRE', icon: '📈' },
    { id: 'cashflow', label: 'Fluxo de Caixa', icon: '💸' },
    { id: 'bank', label: 'Extrato Bancário', icon: '🏦' },
    { id: 'reports', label: 'Relatórios', icon: '📑' },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      {/* Cabeçalho */}
      <div style={{ flexShrink: 0, background: C.surface, padding: '20px 32px 0', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 900, fontSize: 22, color: C.gold }}>Contabilidade</div>
          <div style={{ fontSize: 13, color: C.sub, marginTop: 2 }}>Gestão financeira e contábil da plataforma</div>
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, overflowX: 'auto' }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: C.font,
              padding: '8px 16px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
              color: tab === t.id ? C.gold : C.sub,
              borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
              transition: 'all 0.15s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '24px 32px 40px' }}>
        {tab === 'dashboard' && <AccDashboard />}
        {tab === 'entries' && <AccEntries categories={categories} />}
        {tab === 'categories' && <AccCategories categories={categories} reload={loadCategories} />}
        {tab === 'dre' && <AccDRE />}
        {tab === 'cashflow' && <AccCashFlow />}
        {tab === 'bank' && <AccBank />}
        {tab === 'reports' && <AccReports />}
      </div>
    </div>
  );
}
